import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetSettingsMutation,
  useUpdateBrandSettingsMutation,
} from "redux/settings/settingsSlice";
import { validateMerchantSavingField } from "utils/Validator";
import { useTranslation } from "react-i18next";
import { UTILS } from "utils";
import { errorHandler } from "api/api/errorHandler";
import AppInput from "components/ui/AppInput";
import AppButton from "components/ui/AppButton";

const FIELD_ORDER = [
  "vendorName",
  "vendorBenchmark",
  "restHeroBenchmark",
  "estimatedMonthlySavings",
];

const emptyMerchantSavings = {
  vendorName: "",
  vendorBenchmark: "",
  restHeroBenchmark: 0,
  estimatedMonthlySavings: 0,
};

const mergeMerchantSavings = (ms) => ({
  ...emptyMerchantSavings,
  ...(ms || {}),
});

const FieldsList = {
  vendorName: {
    label: "Vendor Name",
    type: "text",
    required: true,
    editable: true,
  },
  vendorBenchmark: {
    label: "Vendor Benchmark (%)",
    type: "number",
    required: true,
    editable: true,
  },
  restHeroBenchmark: {
    label: "Comera Benchmark (%)",
    type: "number",
    editable: false,
  },
  estimatedMonthlySavings: {
    label: "Estimated Monthly Savings (%)",
    type: "number",
    editable: false,
    calculateValue: (obj) => {
      if (
        obj?.vendorBenchmark === "" ||
        obj?.vendorBenchmark === undefined ||
        obj?.vendorBenchmark === null
      ) {
        return "";
      }
      const v = Number(obj.vendorBenchmark);
      const r = Number(obj?.restHeroBenchmark) || 0;
      if (!Number.isFinite(v)) return "";
      return v - r;
    },
  },
};

const BenchMarkSettings = () => {
  const { settings } = useSelector((state) => state?.SettingsReducer);
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const { t } = useTranslation();

  const [formFields, setFormFields] = useState(() =>
    mergeMerchantSavings(settings?.merchantSavings)
  );
  const [errors, setErrors] = useState({});

  const [updateBrandSettings] = useUpdateBrandSettingsMutation();
  const [getSettings] = useGetSettingsMutation();

  const baseline = useMemo(
    () => mergeMerchantSavings(settings?.merchantSavings),
    [settings?.merchantSavings]
  );

  const handleValueUpdate = (key, rawValue, type) => {
    let value = rawValue;
    if (type === "number") {
      if (rawValue === "" || rawValue === null || rawValue === undefined) {
        value = "";
      } else {
        const n = Number(rawValue);
        value = Number.isNaN(n) ? "" : n;
      }
    }
    const newFormData = { ...formFields, [key]: value };
    setFormFields(newFormData);

    if (FieldsList[key]?.editable) {
      const fieldError = validateMerchantSavingField(
        key,
        type === "number" && value === "" ? "" : String(value)
      );
      setErrors((prev) => ({
        ...prev,
        [key]: fieldError[key],
      }));
    }
  };

  const isSameValues = useMemo(() => {
    const vName = String(formFields.vendorName ?? "").trim();
    const bName = String(baseline.vendorName ?? "").trim();
    const vBench = Number(formFields.vendorBenchmark);
    const bBench = Number(baseline.vendorBenchmark);
    const sameName = vName === bName;
    const sameBench =
      (Number.isFinite(vBench) ? vBench : NaN) ===
      (Number.isFinite(bBench) ? bBench : NaN);
    return sameName && sameBench;
  }, [formFields, baseline]);

  const updateMyBrand = async () => {
    const vendorNameErrors = validateMerchantSavingField(
      "vendorName",
      String(formFields.vendorName ?? "")
    );
    const benchmarkErrors = validateMerchantSavingField(
      "vendorBenchmark",
      formFields.vendorBenchmark === ""
        ? ""
        : String(formFields.vendorBenchmark)
    );
    const allErrors = { ...vendorNameErrors, ...benchmarkErrors };

    setErrors(allErrors);
    if (Object.values(allErrors).some(Boolean)) return;

    try {
      const userDetail = UTILS.getCurrentUser();
      const obj = {
        brandId: settings?.brandId ?? userDetail.brandId,
        language: "EN",
      };
      const vendorNum = Number(formFields.vendorBenchmark);
      const restNum = Number(formFields.restHeroBenchmark ?? 0);
      const merchantSavings = {
        ...(settings?.merchantSavings || {}),
        vendorName: String(formFields.vendorName).trim(),
        vendorBenchmark: vendorNum,
        restHeroBenchmark: restNum,
        estimatedMonthlySavings: vendorNum - restNum,
        customBenchmarkProvided: true,
      };

      await updateBrandSettings({ merchantSavings, ...obj }).unwrap();
      await getSettings(obj);
    } catch (error) {
      UTILS.errorNotification(errorHandler(error));
    }
  };

  useEffect(() => {
    setFormFields(mergeMerchantSavings(settings?.merchantSavings));
  }, [settings?.merchantSavings]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        updateMyBrand();
      }}
    >
      <div className="mt-6 grid grid-flow-row-dense gap-4 md:grid-cols-2">
        {FIELD_ORDER.map((key) => {
          const fieldProps = FieldsList[key];
          if (!fieldProps) return null;
          const value = fieldProps.calculateValue
            ? fieldProps.calculateValue(formFields)
            : formFields[key];
          const error = errors[key] ? t(errors[key]) : null;
          const disabled = !fieldProps.editable;
          const inputType = fieldProps.type === "number" ? "number" : "text";

          return (
            <div key={key}>
              <AppInput
                mandatory={fieldProps.required ?? false}
                label={t(fieldProps.label)}
                type={inputType}
                value={
                  disabled && fieldProps.type === "number"
                    ? value === "" || value === undefined || value === null
                      ? ""
                      : String(value)
                    : value === 0
                      ? 0
                      : value ?? ""
                }
                disabled={disabled}
                error={error}
                onChange={(e) =>
                  handleValueUpdate(
                    key,
                    e.target.value,
                    fieldProps.type ?? "text"
                  )
                }
                inputProps={
                  fieldProps.type === "number" && fieldProps.editable
                    ? { min: 0, step: "any" }
                    : {}
                }
              />
            </div>
          );
        })}
      </div>
      <div className="mt-6">
        <AppButton
          type="submit"
          variant="primary"
          disabled={isSameValues || isLoading}
          loading={isLoading}
        >
          {t("Save benchmark settings")}
        </AppButton>
      </div>
    </form>
  );
};

export default BenchMarkSettings;
